'use client';

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { Map, List, Loader2, MapPin, Expand, X } from 'lucide-react';
import { EntityCard } from './EntityCard';
import { InitiativeCard } from './initiatives/InitiativeCard';
import { useEntityDetailSheet, buildEntityNavigationList } from '@/lib/hooks/useEntityDetailSheet';
import { buildInitiativeNavigationList } from '@/lib/hooks/useInitiativeDetailSheet';
import { BrowserNavigationProvider } from '@/lib/ui/BrowserNavigationContext';
import type { Entity, ApiInitiative } from '@/lib/api';

// Dynamically import the map to avoid SSR issues with Leaflet
const ObjectMapView = dynamic(() => import('./ObjectMapView').then((mod) => mod.default), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-xl">
      <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
    </div>
  ),
}) as React.ComponentType<{
  objects: MapBrowserObject[];
  center: [number, number];
  zoom: number;
  hoveredId: string | number | null;
  onMarkerHover: (id: string | number | null) => void;
  onShowDetail?: (obj: MapBrowserObject) => void;
  onViewChange?: (center: [number, number], zoom: number) => void;
  legend?: MapLegendConfig;
}>;

// --- Types ---

/**
 * Highlight level for a map marker. Controls visual prominence.
 * - 'running' → currently running (between start and end date)
 * - 'today'   → starts today (no end date)
 * - 'high'    → starting very soon (within 3 days)
 * - 'medium'  → starting within 7 days
 * - undefined  → default marker
 */
export type MarkerHighlightLevel = 'running' | 'today' | 'high' | 'medium';

export interface MapBrowserObject {
  id: string | number;
  objectType: 'entity' | 'initiative';
  lat?: number;
  lng?: number;
  title?: string;
  image?: string;
  /** Link to the object's detail page */
  href?: string;
  /** Tags to display in the map popup */
  tags?: string[];
  /** Formatted date range string to display in the map popup */
  dateRange?: string;
  /** The raw data object (Entity, ApiInitiative, etc.) */
  data: any;
  /** Optional highlight level that controls marker visual prominence */
  highlight?: MarkerHighlightLevel;
}

/** A single entry in the map legend / key overlay */
export interface MapLegendItem {
  color: string;
  label: string;
}

/** Configuration for the map legend overlay */
export interface MapLegendConfig {
  title?: string;
  items: MapLegendItem[];
}

// --- Shared initiative highlight helpers ---

/** Compute a highlight level based on initiative start/end dates */
export function getInitiativeHighlight(
  startDate?: string | null,
  endDate?: string | null
): MarkerHighlightLevel | undefined {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(todayStart.getTime() + 86400000 - 1);

  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  // "Running Now": today is between start and end (inclusive) when both specified
  if (
    start &&
    end &&
    todayStart >= new Date(start.getFullYear(), start.getMonth(), start.getDate()) &&
    todayEnd <= new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999)
  ) {
    return 'running';
  }

  // "Running Now": no start date but end date is today or later
  if (
    !start &&
    end &&
    todayStart <= new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999)
  ) {
    return 'running';
  }

  // "Today": start date is today and no end date
  if (start && !end) {
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    if (startDay.getTime() === todayStart.getTime()) {
      return 'today';
    }
  }

  // Future start date tiers
  if (start) {
    const diffMs = start.getTime() - now.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > 0 && diffDays <= 3) return 'high';
    if (diffDays > 3 && diffDays <= 7) return 'medium';
  }

  return undefined;
}

/** Shared legend config for initiative timing on maps */
export const INITIATIVE_TIMING_LEGEND: MapLegendConfig = {
  title: 'When',
  items: [
    { color: '#16a34a', label: 'Running now' },
    { color: '#dc2626', label: 'Today' },
    { color: '#f97316', label: 'Next 3 days' },
    { color: '#facc15', label: 'Next 7 days' },
  ],
};

export interface MapBrowserFetcherResult {
  objects: MapBrowserObject[];
  hasNextPage: boolean;
  nextCursor?: string;
}

export interface ObjectMapBrowserProps {
  /** Async fetcher function. Receives cursor for pagination. */
  fetcher: (params: { cursor?: string; limit: number }) => Promise<MapBrowserFetcherResult>;
  /** Initial map center [lat, lng] */
  initialCenter?: [number, number];
  /** Initial map zoom */
  initialZoom?: number;
  /** Whether map is shown by default */
  defaultShowMap?: boolean;
  /** Page size */
  limit?: number;
  /** Optional className */
  className?: string;
  /** Optional empty state */
  emptyState?: { title: string; description: string };
  /** Render a custom card for an object. Falls back to EntityCard / InitiativeCard */
  renderCard?: (object: MapBrowserObject, isHovered: boolean) => React.ReactNode;
  /** Show the result count header (e.g. "Showing 12 results"). Defaults to true. */
  showResultCount?: boolean;
}

export function ObjectMapBrowser({
  fetcher,
  initialCenter = [-41.2865, 174.7762], // Wellington NZ default
  initialZoom = 6,
  defaultShowMap = true,
  limit = 20,
  className,
  emptyState,
  renderCard,
  showResultCount = false,
}: ObjectMapBrowserProps) {
  const [showMap, setShowMap] = useState(defaultShowMap);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'map'>('list');
  const [objects, setObjects] = useState<MapBrowserObject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hoveredId, setHoveredId] = useState<string | number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>(initialCenter);
  const [mapZoom, setMapZoom] = useState(initialZoom);
  const listRef = useRef<HTMLDivElement>(null);
  const { showEntityDetail } = useEntityDetailSheet();

  const handleShowDetail = useCallback(
    (obj: MapBrowserObject) => {
      if (obj.objectType === 'entity') {
        const entity = obj.data as Entity & { entityType?: string };
        const entityObjects = objects.filter((o) => o.objectType === 'entity');
        const navigationList =
          entityObjects.length > 1
            ? buildEntityNavigationList(
                entityObjects.map((o) => o.data as Entity),
                entity.entityType || entity.type || 'brand'
              )
            : undefined;
        showEntityDetail(entity.id, entity.entityType || entity.type || 'brand', {
          title: entity.name_1,
          navigationList,
        });
      }
    },
    [showEntityDetail, objects]
  );

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetcher({ limit })
      .then((result) => {
        if (cancelled) return;
        setObjects(result.objects);
        setHasNextPage(result.hasNextPage);
        setNextCursor(result.nextCursor);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetcher, limit]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasNextPage) return;
    setIsLoadingMore(true);
    try {
      const result = await fetcher({ cursor: nextCursor, limit });
      setObjects((prev) => {
        // Deduplicate
        const existingIds = new Set(prev.map((o) => `${o.objectType}-${o.id}`));
        const newObjects = result.objects.filter(
          (o) => !existingIds.has(`${o.objectType}-${o.id}`)
        );
        return [...prev, ...newObjects];
      });
      setHasNextPage(result.hasNextPage);
      setNextCursor(result.nextCursor);
    } catch (err: any) {
      setError(err?.message || 'Failed to load more');
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetcher, limit, nextCursor, hasNextPage, isLoadingMore]);

  // Objects that have coordinates for the map
  const mappableObjects = useMemo(
    () => objects.filter((o) => o.lat != null && o.lng != null),
    [objects]
  );

  const totalCount = objects.length;

  const handleViewChange = useCallback((center: [number, number], zoom: number) => {
    setMapCenter(center);
    setMapZoom(zoom);
  }, []);

  // Shared map element — rendered once, used in both desktop and mobile layouts
  const desktopMapElement = (
    <ObjectMapView
      objects={mappableObjects}
      center={mapCenter}
      zoom={mapZoom}
      hoveredId={hoveredId}
      onMarkerHover={setHoveredId}
      onShowDetail={handleShowDetail}
      onViewChange={handleViewChange}
    />
  );

  const mobileMapElement = (
    <ObjectMapView
      objects={mappableObjects}
      center={mapCenter}
      zoom={mapZoom}
      hoveredId={hoveredId}
      onMarkerHover={setHoveredId}
      onShowDetail={handleShowDetail}
      onViewChange={handleViewChange}
    />
  );

  // --- Render card by type ---
  const renderObjectCard = useCallback(
    (obj: MapBrowserObject) => {
      const isHovered = hoveredId === obj.id;

      if (renderCard) {
        return renderCard(obj, isHovered);
      }

      switch (obj.objectType) {
        case 'entity': {
          const entity = obj.data as Entity & { entityType?: string };
          return (
            <EntityCard
              entity={entity}
              entityType={entity.entityType || entity.type || 'brand'}
              variant="compact"
            />
          );
        }
        case 'initiative': {
          const initiative = obj.data as ApiInitiative;
          return <InitiativeCard initiative={initiative} variant="compact" />;
        }
        default:
          return null;
      }
    },
    [hoveredId, renderCard]
  );

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-10 h-10 text-gray-400 animate-spin" />
      </div>
    );
  }

  // --- Error state ---
  if (error && objects.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 cursor-pointer"
        >
          Retry
        </button>
      </div>
    );
  }

  // --- Empty state ---
  if (objects.length === 0) {
    return (
      <div className="text-center py-16">
        <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {emptyState?.title || 'No results found'}
        </h3>
        <p className="text-gray-500 text-sm">
          {emptyState?.description || 'Try adjusting your filters'}
        </p>
      </div>
    );
  }

  // --- Build navigation lists from loaded objects for card context ---
  const entityNavList = useMemo(() => {
    const entityObjs = objects.filter((o) => o.objectType === 'entity');
    if (entityObjs.length <= 1) return undefined;
    return buildEntityNavigationList(
      entityObjs.map((o) => o.data as Entity),
      'brand'
    );
  }, [objects]);

  const initiativeNavList = useMemo(() => {
    const initObjs = objects.filter((o) => o.objectType === 'initiative');
    if (initObjs.length <= 1) return undefined;
    return buildInitiativeNavigationList(initObjs.map((o) => o.data as ApiInitiative));
  }, [objects]);

  return (
    <BrowserNavigationProvider
      entityNavigationList={entityNavList}
      initiativeNavigationList={initiativeNavList}
    >
      <div className={clsx('w-full relative flex-1 flex flex-col', className)}>
        {/* Desktop toolbar - hidden on mobile */}
        <div className="hidden lg:flex items-center justify-between mb-4">
          {showResultCount && (
            <p className="text-sm text-gray-500">
              Showing {totalCount} result{totalCount !== 1 ? 's' : ''}
              {mappableObjects.length > 0 && showMap && (
                <span className="ml-1">· {mappableObjects.length} on map</span>
              )}
            </p>
          )}
          <button
            onClick={() => {
              setShowMap((v) => !v);
              setMapExpanded(false);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ml-auto"
            aria-label={showMap ? 'Hide map' : 'Show map'}
          >
            {showMap ? <List className="w-4 h-4" /> : <Map className="w-4 h-4" />}
            {showMap ? 'Hide map' : 'Show map'}
          </button>
        </div>

        {/* Mobile toolbar */}
        {showResultCount && (
          <div className="flex lg:hidden items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              Showing {totalCount} result{totalCount !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Desktop: side-by-side layout — map always mounted, visibility toggled */}
        <div className={clsx('hidden lg:flex gap-4', showMap ? 'flex-row' : '')}>
          {/* Card list */}
          {!mapExpanded && (
            <div
              ref={listRef}
              className={clsx(
                'overflow-y-auto',
                showMap ? 'w-1/2 xl:w-[45%] max-h-[calc(100vh-12rem)]' : 'w-full'
              )}
            >
              <div
                className={clsx(
                  'grid gap-3',
                  showMap
                    ? 'grid-cols-3'
                    : 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-5'
                )}
              >
                {objects.map((obj) => (
                  <div
                    key={`${obj.objectType}-${obj.id}`}
                    onMouseEnter={() => setHoveredId(obj.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    className={clsx(
                      'transition-all duration-200 rounded-xl',
                      hoveredId === obj.id && 'shadow-md ring-1 ring-gray-300 scale-[1.02]'
                    )}
                  >
                    {renderObjectCard(obj)}
                  </div>
                ))}
              </div>

              {hasNextPage && (
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="w-full mt-4 px-4 py-2.5 bg-primary-500 text-white font-medium rounded-xl hover:bg-primary-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isLoadingMore ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          )}

          {/* Desktop map panel — always mounted, visibility toggled */}
          <div
            className={clsx(
              'h-[calc(100vh-12rem)] rounded-xl overflow-hidden border border-gray-200 sticky top-4 relative',
              showMap ? (mapExpanded ? 'w-full' : 'w-1/2 xl:w-[55%]') : 'hidden'
            )}
          >
            <button
              onClick={() => setMapExpanded((v) => !v)}
              className="absolute top-3 right-3 z-[1000] w-10 h-10 bg-white rounded-lg shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
              aria-label={mapExpanded ? 'Shrink map' : 'Expand map'}
            >
              {mapExpanded ? (
                <X className="w-5 h-5 text-gray-700" />
              ) : (
                <Expand className="w-5 h-5 text-gray-700" />
              )}
            </button>
            {desktopMapElement}
          </div>
        </div>

        {/* Mobile: toggled list or map — both always mounted, visibility toggled */}
        <div className="lg:hidden relative min-h-[calc(100vh-12rem)]">
          <div className={clsx(mobileView !== 'list' && 'hidden')}>
            <div className="grid grid-cols-2 gap-3">
              {objects.map((obj) => (
                <div key={`${obj.objectType}-${obj.id}`} className="transition-shadow rounded-xl">
                  {renderObjectCard(obj)}
                </div>
              ))}
            </div>

            {hasNextPage && (
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="w-full mt-4 px-4 py-2.5 bg-primary-500 text-white font-medium rounded-xl hover:bg-primary-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isLoadingMore ? 'Loading...' : 'Load more'}
              </button>
            )}
          </div>

          <div
            className={clsx(
              'rounded-xl overflow-hidden border border-gray-200',
              mobileView === 'map' ? 'h-[calc(100vh-12rem)]' : 'hidden'
            )}
          >
            {mobileMapElement}
          </div>
        </div>

        {/* Mobile toggle button — sticky at bottom */}
        {objects.length > 0 && (
          <div className="lg:hidden sticky bottom-0 z-[1001] pb-2 pt-2 flex justify-center pointer-events-none">
            <button
              onClick={() => {
                setMobileView((v) => (v === 'list' ? 'map' : 'list'));
                window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
              }}
              className="flex items-center gap-2 px-5 py-3 bg-gray-900 text-white text-sm font-medium rounded-full shadow-lg hover:bg-gray-800 transition-colors cursor-pointer pointer-events-auto"
            >
              {mobileView === 'list' ? (
                <>
                  <Map className="w-4 h-4" />
                  Show map
                </>
              ) : (
                <>
                  <List className="w-4 h-4" />
                  Show list
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </BrowserNavigationProvider>
  );
}
